/**
 * UserCard Component
 * Card for Ritualists / Members catalog pages.
 * Same layout as ExpertCard but WITHOUT the "BOOK A SESSION" CTA.
 * Shows avatar, name+handle, followers+location, bio, tags,
 * optional bottom metric, and message+follow icon buttons.
 *
 * Uses text-text-primary / text-text-body which resolve correctly in .dark context.
 */

import { memo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ChatBubbleIcon, HeartFilledIcon, HeartOutlineIcon } from "@/components/icons"
import { formatFollowerCount } from "@/components/shared/formatFollowerCount"
import { formatLocation } from "@/components/shared/formatLocation"
import { TagList } from "@/components/shared/TagList"
import { Avatar } from "@/components/ui/avatar"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useCreateDmConversation } from "@/features/chats/hooks/useDmChat"
import type { CatalogUser } from "../types/catalog.types"

interface UserCardProps {
  user: CatalogUser
  onFollow?: (userId: string) => void
  onUnfollow?: (userId: string) => void
  className?: string
}

export const UserCard = memo(function UserCard({
  user,
  onFollow,
  onUnfollow,
  className,
}: UserCardProps) {
  const navigate = useNavigate()
  const currentUser = useAuthStore(s => s.user)
  const isSelf = currentUser?.id === user.id
  const isGuest = !currentUser
  const [showLoginModal, setShowLoginModal] = useState(false)
  const { mutate: createConversation, isPending: isCreatingConversation } =
    useCreateDmConversation()
  const location = formatLocation(user.city, user.state)

  return (
    <>
      <div
        className={`bg-white/5 border border-white/10 rounded-[20px] overflow-hidden relative p-5 flex flex-col ${className || ""}`}
      >
        {/* Top row: Avatar + Name/Handle + Action Icons */}
        <div className="flex items-start gap-4 mb-4 overflow-hidden">
          {/* Avatar - 90px square with rounded-xl */}
          <button
            type="button"
            onClick={() => navigate(`/profile/${user.id}`)}
            className="shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
            aria-label={`View ${user.name}'s profile`}
          >
            <Avatar
              src={user.profilePictureUrl ?? undefined}
              fallback={user.name || "?"}
              size="lg"
              className="w-[90px] h-[90px] rounded-xl"
            />
          </button>

          {/* Name + Handle + Followers/Location */}
          <div className="flex-1 min-w-0 pt-1 overflow-hidden">
            <div className="flex flex-col items-start gap-1 min-w-0">
              <span className="w-full text-[13px] md:text-[18px] font-semibold text-text-primary leading-[22px] truncate block">
                {user.name}
              </span>
              <span className="w-full text-sm text-text-primary/90 leading-[22px] truncate block hidden md:flex">
                @{user.username}
              </span>
            </div>
            <div className="flex flex-col md:flex-row md:items-center md:gap-2.5 mt-2 text-xs md:text-[13px] text-text-primary/70 leading-[20px] min-w-0 truncate">
              <span className="truncate">{formatFollowerCount(user.followerCount)} followers</span>
              {location && <span className="truncate">{location}</span>}
            </div>
          </div>

          {/* Action icons - top right. Hidden when the card represents the
            current user — chatting/following yourself is meaningless. */}
          {!isSelf && (
            <div className="flex items-center gap-2.5 shrink-0">
              <button
                type="button"
                className="w-11 h-11 rounded-[10px] border-gradient-glass flex items-center justify-center transition-colors disabled:opacity-50"
                onClick={e => {
                  e.stopPropagation()
                  if (isGuest) {
                    setShowLoginModal(true)
                    return
                  }
                  createConversation(user.id, {
                    onSuccess: conversation => {
                      navigate(`/chats/dm/${conversation.id}`)
                    },
                  })
                }}
                disabled={isCreatingConversation}
                title="Chat"
              >
                <ChatBubbleIcon className="w-[22px] h-[22px] text-text-primary/70" />
              </button>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  if (isGuest) {
                    setShowLoginModal(true)
                    return
                  }
                  user.isFollowing ? onUnfollow?.(user.id) : onFollow?.(user.id)
                }}
                className="w-11 h-11 rounded-[10px] border-gradient-glass flex items-center justify-center hover:bg-white/10 transition-colors"
                aria-label={user.isFollowing ? "Unfollow" : "Follow"}
              >
                {user.isFollowing ? (
                  <HeartFilledIcon className="w-[22px] h-[22px] text-text-primary/70" />
                ) : (
                  <HeartOutlineIcon className="w-[22px] h-[22px] text-text-primary/70" />
                )}
              </button>
            </div>
          )}
        </div>

        {/* Bio */}
        {user.bio && (
          <p className="text-sm text-text-primary leading-[25px] line-clamp-2 mt-1">{user.bio}</p>
        )}

        {/* Tags */}
        <TagList tags={user.tags} className="mt-4.5" searchTab="ritualists" />

        {/* Bottom row: Optional metric */}
        {user.metricCount != null && user.metricLabel && (
          <div className="mt-7">
            <p className="text-sm text-text-primary leading-[20px]">
              {user.metricCount} {user.metricLabel}
            </p>
          </div>
        )}
      </div>
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </>
  )
})
